// covered-by: frontends/carrie/src/routes/settings/routes/knowledge-database/components/__tests__/SourceViewerDialog.test.tsx
import { Dialog, Flex, Spinner, Text } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PdfCitationPreview } from "./PdfCitationPreview";

import { SpreadsheetView } from "../../../../../components/SpreadsheetView";
import { useObjectUrl } from "../../../../../hooks/useObjectUrl";
import { type KnowledgeRuleCitationResult } from "../../../../../utils/parsers/knowledge";
import { useSourceFileQuery } from "../../../../../utils/queries/knowledge";

/**
 * Citation viewer: renders the cited source from the per-source byte cache
 * (stored citations carry denormalized coordinates, never URLs — presigned
 * links go stale, and re-fetching the whole file per open is what the cache
 * exists to prevent), then dispatches on the file type:
 * - PDF -> PdfCitationPreview opened at the cited page with a pin at
 *   rel_x/rel_y
 * - spreadsheet -> SpreadsheetView with the citation's cellAnchor highlight
 *   (fed an object URL over the cached bytes — parsed locally, never
 *   re-downloaded)
 * - image -> plain <img> with a pin
 * - anything else -> the verbatim quote (quote-only citation)
 *
 * why: Dialog.Root must flip `open` false on dismiss — literal `open` + parent
 * nulling citation left the Radix overlay up (same race as RuleDetailPanel).
 */
export function SourceViewerDialog({
  citation,
  fileName,
  onClose,
}: {
  citation: KnowledgeRuleCitationResult | null;
  fileName?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation("ui");
  const file = useSourceFileQuery(citation?.sourceId ?? undefined);
  const [dialogOpen, setDialogOpen] = useState(true);
  const [openCitationId, setOpenCitationId] = useState(
    citation?.extractionId ?? citation?.sourceId,
  );

  const mimeType = file.data?.mimeType ?? "";
  const bytes = file.data?.bytes;

  const isPdf = mimeType === "application/pdf";
  const isSpreadsheet =
    mimeType.includes("spreadsheet") ||
    mimeType === "text/csv" ||
    mimeType === "application/vnd.ms-excel";
  const isImage = mimeType.startsWith("image/");

  // URL-based renderers (img src, SpreadsheetView's fetch) get an in-memory
  // object URL over the cached bytes instead of a fresh presigned download.
  const objectUrl = useObjectUrl(
    isSpreadsheet || isImage ? bytes : undefined,
    mimeType,
  );

  // No `citation &&` prefix on purpose: RuleDetailPanel renders this viewer
  // unconditionally, so a dismiss only `return null`s it — the state survives.
  // The null transition has to reset openCitationId to undefined, or clicking
  // the SAME citation again matches the stale key, skips the reset, and leaves
  // dialogOpen false (nothing opens). Mirrors RuleDetailPanel's rule?._id reset.
  const citationKey = citation?.extractionId ?? citation?.sourceId;
  if (citationKey !== openCitationId) {
    setOpenCitationId(citationKey);
    setDialogOpen(true);
  }

  if (!citation) return null;

  const location = citation.location;
  // Captured as a const so renderBody (a closure) keeps the null-narrowing —
  // TS does not narrow captured parameters inside nested functions.
  const quote = citation.quote;

  function renderBody() {
    // The object URL for URL-based renderers is minted in an effect, one
    // render after the bytes land — hold the spinner for that frame instead
    // of flashing the quote fallback.
    if (file.isLoading || ((isSpreadsheet || isImage) && bytes && !objectUrl)) {
      return (
        <Flex justify="center" className="py-8">
          <Spinner />
        </Flex>
      );
    }
    if (file.isError || !bytes) {
      return (
        <Text size="2" color="red" data-testid="knowledge-source-viewer-error">
          {t("settings.sourceViewerDialog.sourceFileCouldNotLoaded")}
        </Text>
      );
    }

    if (isPdf) {
      return (
        <PdfCitationPreview
          data={bytes}
          pageNumber={location.pageNumber ?? 1}
          relX={location.rel_x}
          relY={location.rel_y}
          bbox={location.bbox}
        />
      );
    }
    if (isSpreadsheet && objectUrl) {
      return (
        <div data-testid="knowledge-source-viewer-spreadsheet">
          <SpreadsheetView
            url={objectUrl}
            highlight={
              location.cellAnchor
                ? {
                    sheetIndex: location.cellAnchor.sheetIndex,
                    sheetName: location.cellAnchor.sheetName,
                    range: location.cellAnchor.range,
                    row: location.cellAnchor.row,
                  }
                : null
            }
          />
        </div>
      );
    }
    if (isImage && objectUrl) {
      return (
        <div className="relative">
          <img
            src={objectUrl}
            alt={fileName ?? "Cited source"}
            className="max-w-full"
            data-testid="knowledge-source-viewer-image"
          />
          {location.rel_x != null && location.rel_y != null ? (
            <span
              aria-label={t("settings.sourceViewerDialog.citationLocation")}
              className="border-white bg-blue-9 absolute block size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-solid shadow-md"
              style={{
                left: `${location.rel_x * 100}%`,
                top: `${location.rel_y * 100}%`,
              }}
              data-testid="knowledge-source-viewer-pin"
            />
          ) : null}
        </div>
      );
    }
    return (
      <Text
        size="2"
        color="gray"
        className="italic"
        data-testid="knowledge-source-viewer-quote"
      >
        “{quote}”
      </Text>
    );
  }

  return (
    <Dialog.Root
      open={dialogOpen}
      onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) onClose();
      }}
    >
      <Dialog.Content
        className="max-w-4xl"
        aria-label={`Citation in ${fileName ?? "source document"}`}
        data-testid="knowledge-source-viewer-dialog"
      >
        <Dialog.Title size="3">
          {fileName ?? t("settings.sourceViewerDialog.sourceDocument")}
        </Dialog.Title>
        <Dialog.Description size="1" color="gray" className="mb-3">
          “{quote}”
        </Dialog.Description>
        {renderBody()}
      </Dialog.Content>
    </Dialog.Root>
  );
}
